/**
 * @copyright
 * Copyright (c) 2025 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */
import { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { getRouteConfig } from "../../../../config/route.config";
import { AccountInfoStatus, RequestStatus } from "~/constants";
import TwoFactorAuthDialog from "~/features/auth/components/TwoFactorAuthDialog";
import TwoFactorAuthInterstitialWrapper, {
  TwoFactorInterstitialType,
} from "~/features/auth/components/TwoFactorAuthInterstitialWrapper";
import {
  selectAuthViewOnly,
  selectResellerConfig,
  selectTwoFactorAuthDifferentSession,
  selectTwoFactorAuthRequired,
  selectTwoFactorAuthSessionCheckStatus,
  selectTwoFactorAuthTokenValidationStatus,
} from "~/features/auth/selectors";
import { check2FASession } from "~/features/auth/thunks";
import { selectUGComplianceStatus } from "~/features/userGuide/selectors";
import { AppDispatch } from "~/state/types";
import { ComponentBaseProps } from "~/types";

const TwoFactorAuthWrapper: FC<ComponentBaseProps> = ({ children }) => {
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();

  const routeConfig = getRouteConfig(location.pathname);
  const isProtectedRoute = routeConfig.twoFactorAuth;
  const canBypass = routeConfig.canBypassTwoFactorAuth;
  const need2FA = useSelector(selectTwoFactorAuthRequired);
  const tokenValidationStatus = useSelector(selectTwoFactorAuthTokenValidationStatus);
  const sessionCheckStatus = useSelector(selectTwoFactorAuthSessionCheckStatus);
  const isDifferentSession = useSelector(selectTwoFactorAuthDifferentSession);
  const isViewOnly = useSelector(selectAuthViewOnly);
  const complianceStatus = useSelector(selectUGComplianceStatus);
  const [showInterstitial, setShowInterstitial] = useState(true);
  const resellerConfig = useSelector(selectResellerConfig);
  const globalBypassEnabled = resellerConfig?.globalTfaBypass ? resellerConfig.globalTfaBypass > Date.now() : false;

  const disable2FA =
    !isProtectedRoute ||
    isViewOnly ||
    globalBypassEnabled ||
    (canBypass && complianceStatus === AccountInfoStatus.MISSING);
  useEffect(() => {
    if (!disable2FA && tokenValidationStatus === RequestStatus.IDLE) {
      dispatch(check2FASession())
        .unwrap()
        .catch(() => {});
    }
    // eslint-disable-next-line
  }, [disable2FA, tokenValidationStatus]);

  if (disable2FA) {
    return <>{children}</>;
  }

  if (showInterstitial) {
    if (tokenValidationStatus === RequestStatus.ERROR) {
      if (isDifferentSession) {
        return (
          <TwoFactorAuthInterstitialWrapper
            setIsOpen={setShowInterstitial}
            type={TwoFactorInterstitialType.differentSession}
          >
            {children}
          </TwoFactorAuthInterstitialWrapper>
        );
      }

      return (
        <TwoFactorAuthInterstitialWrapper setIsOpen={setShowInterstitial} type={TwoFactorInterstitialType.error}>
          {children}
        </TwoFactorAuthInterstitialWrapper>
      );
    }

    if (tokenValidationStatus === RequestStatus.SUCCESS) {
      return (
        <TwoFactorAuthInterstitialWrapper setIsOpen={setShowInterstitial} type={TwoFactorInterstitialType.success}>
          {children}
        </TwoFactorAuthInterstitialWrapper>
      );
    }
  }

  if (sessionCheckStatus === RequestStatus.SUCCESS || tokenValidationStatus === RequestStatus.SUCCESS) {
    return <>{children}</>;
  }

  if (need2FA) {
    return <TwoFactorAuthDialog open={true} />;
  }

  return null;
};

export default TwoFactorAuthWrapper;
